import { Link, useParams } from 'react-router-dom'
import { ArrowRight, MapPin } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import {
  formatFeeCents,
  getBodyTypeBySlug,
  getCityBySlug,
  getStateBySlug,
} from '@/data/shop'
import { useSeo } from '@/lib/seo'

export function ShopSlugPage() {
  const { slug } = useParams<{ slug: string }>()

  const bodyType = slug ? getBodyTypeBySlug(slug) : undefined
  const state = slug ? getStateBySlug(slug) : undefined

  if (bodyType) {
    useSeo({
      title: bodyType.seo.title,
      description: bodyType.seo.description,
      image: `/shop-images/body-types/${bodyType.slug}.jpg`,
    })
  } else if (state) {
    useSeo({
      title: state.seo.title,
      description: state.seo.description,
      image: `/shop-images/states/${state.slug}.jpg`,
    })
  }

  if (!slug || (!bodyType && !state)) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">Not found</h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that shop page. Try browsing the shop hub.
              </p>
              <Link to="/shop" className="inline-block mt-6">
                <Button>Back to Shop</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (bodyType) {
    const heroImageUrl = `/shop-images/body-types/${bodyType.slug}.webp`
    return (
      <div className="min-h-screen bg-white">
        {/* Hero - match BrandPage layout */}
        <header className="relative min-h-[70vh] flex items-start justify-center pt-28 md:pt-32 pb-16 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-gray-900"
            style={{ backgroundImage: `url('${heroImageUrl}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
          <div className="absolute inset-0 bg-black/35" />

          <div className="relative z-10 w-full">
            <div className="text-center px-6 max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-6 text-sm">
                <Link to="/shop" className="text-white hover:text-white/90 transition-colors">
                  Shop
                </Link>
                <span className="text-white">/</span>
                <span className="text-white font-medium">{bodyType.pluralName}</span>
              </div>

              <div className="mb-8">
                <h1 className="font-display text-5xl md:text-6xl text-white mb-4 leading-tight tracking-tight">
                  {bodyType.pluralName}
                </h1>
                <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
                  Browse {bodyType.vehicleCount.toLocaleString()} vehicles in our {bodyType.pluralName.toLowerCase()} collection
                </p>
                <div className="w-24 h-px bg-white mx-auto mt-6" />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to={`/vehicles?bodyType=${encodeURIComponent(bodyType.slug)}`}>
                  <Button size="lg">
                    Browse inventory <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/apply">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Apply for Financing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="bg-gray-50">
          <div className="container-page py-8">
            <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{bodyType.tagline}</h2>
                      <p className="mt-2 text-gray-600">{bodyType.description}</p>
                    </div>
                    <Badge variant="default">{bodyType.vehicleCount.toLocaleString()} vehicles</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">Price tiers</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Quick links are preserved from the client dataset.
                  </p>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {bodyType.priceTiers.map((tier) => (
                      <Link
                        key={tier.label}
                        to={`/vehicles?${tier.searchParams}`}
                        className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
                      >
                        <p className="font-semibold text-gray-900">{tier.label}</p>
                        <p className="mt-1 text-sm text-gray-600">{tier.count.toLocaleString()} vehicles</p>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">Popular brands</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {bodyType.popularBrands.map((b) => (
                      <Link
                        key={b}
                        to={`/brands/${encodeURIComponent(b)}`}
                        className="badge bg-gray-100 text-gray-700"
                      >
                        {b}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary-50 border-primary-200">
                <CardContent>
                  <h3 className="font-semibold text-gray-900">Note</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    This page is rendered from the client’s Next.js content copied under `/br/`.
                  </p>
                  <Link to="/shop" className="inline-block mt-4">
                    <Button>Back to Shop</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // State page (slug matched a state)
  const cities = state?.cities ?? []

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-page py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/shop" className="hover:underline">
              Shop
            </Link>
            <span>/</span>
            <span className="font-medium text-gray-900">
              {state?.name} ({state?.abbreviation})
            </span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">
            Used Cars in {state?.name}
          </h1>
          <p className="mt-2 text-gray-600 max-w-3xl">
            Browse {state?.vehicleCount.toLocaleString()} vehicles available for delivery in{' '}
            {state?.name}. These regional landing pages are pulled from the client’s dataset.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to={`/vehicles?state=${encodeURIComponent(state?.slug ?? '')}`}>
              <Button size="lg">
                Browse inventory <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/apply">
              <Button size="lg" variant="outline">
                Apply for Financing
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>Delivery estimate</span>
                    </div>
                    <p className="mt-2 text-gray-900 font-semibold">
                      {state?.deliveryDays.min}–{state?.deliveryDays.max} days
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Sales tax: {state?.salesTaxRate}% · Registration fee:{' '}
                      {formatFeeCents(state?.registrationFee ?? 0)} · Title fee:{' '}
                      {formatFeeCents(state?.titleFee ?? 0)}
                    </p>
                  </div>
                  <Badge variant="default">{state?.vehicleCount.toLocaleString()} vehicles</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">
                  City landing pages
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {cities.length} cities available in the client dataset for this state.
                </p>

                {cities.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-600">
                    No city pages are defined for this state in the supplied content.
                  </p>
                ) : (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {cities.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/shop/${state?.slug}/${c.slug}`}
                        className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
                      >
                        <p className="font-semibold text-gray-900">{c.name}</p>
                        <p className="mt-1 text-sm text-gray-600">
                          {c.vehicleCount.toLocaleString()} vehicles
                        </p>
                        <p className="mt-2 text-xs text-gray-500 line-clamp-2">{c.description}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Popular makes</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(state?.popularMakes ?? []).map((m) => (
                    <span key={m} className="badge bg-gray-100 text-gray-700">
                      {m}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Popular body types</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(state?.popularBodyTypes ?? []).map((bt) => (
                    <span key={bt} className="badge bg-gray-100 text-gray-700">
                      {bt}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick sanity check for city lookup */}
            {cities.length > 0 && (
              <Card className="bg-primary-50 border-primary-200">
                <CardContent>
                  <h3 className="font-semibold text-gray-900">Example city</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    The first city resolves via the same helper used in the client app:{' '}
                    <span className="font-medium">
                      {getCityBySlug(state?.slug ?? '', cities[0].slug)?.name ?? '—'}
                    </span>
                    .
                  </p>
                  <Link to={`/shop/${state?.slug}/${cities[0].slug}`} className="inline-block mt-4">
                    <Button>Open city page</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

